import { useUser } from '@/hooks/use-user';
import { useParams, useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import type { UserWithCommunities } from '@/types';
import { resolveCommunityLandingPath } from '@/lib/community-navigation';
import { hasCommunityPermission, type CommunityPermission } from '@/lib/community-permissions';

interface PermissionGuardProps {
  children: React.ReactNode;
  permission: CommunityPermission;
}

export function PermissionGuard({ children, permission }: PermissionGuardProps) {
  const { communityId } = useParams();
  const navigate = useNavigate();
  const { communities, isLoading, user } = useUser();

  const currentCommunity = communities.find((c) => c.id === communityId) as
    | UserWithCommunities['communities'][number]
    | undefined;

  const allowed = hasCommunityPermission(currentCommunity?.permissions, permission);

  useEffect(() => {
    if (!isLoading && user && currentCommunity && !allowed) {
      navigate(resolveCommunityLandingPath(currentCommunity, user), { replace: true });
    }
  }, [isLoading, user, currentCommunity, allowed, navigate, communityId]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!allowed) {
    return null;
  }

  return <>{children}</>;
}
